import { useState } from "react";
import { toast } from "sonner";
import { Mail, MessageCircle, Building, Briefcase, LifeBuoy, Handshake } from "lucide-react";
import GradientBg from "@/components/common/GradientBg";
import HeadMeta from "@/components/common/HeadMeta";
import Reveal from "@/components/marketing/Reveal";
import api from "@/lib/api";
import { BRAND } from "@/branding.config";

const DEPARTMENTS = [
  { key: "sales",      label: "Sales",       icon: Briefcase, blurb: "New deployments, enterprise pricing, RFPs.", email: () => BRAND.sales_email,   sla: "1 business day" },
  { key: "support",    label: "Support",     icon: LifeBuoy,  blurb: "Existing customer technical issues.",         email: () => BRAND.support_email, sla: "1 business hour" },
  { key: "enterprise", label: "Enterprise",  icon: Handshake, blurb: "MSA, DPA, security review, procurement.",     email: () => BRAND.sales_email,   sla: "Same day" },
  { key: "general",    label: "General",     icon: Mail,      blurb: "Everything else — press, careers, partners.", email: () => BRAND.contact_email, sla: "2 business days" },
];

const OFFICES = [
  { city: "Zurich",    country: "Switzerland (HQ)", tz: "CET",  addr: "Bahnhofstrasse 1 · 8001 Zurich" },
  { city: "New York",  country: "USA",              tz: "EST",  addr: "125 Broadway · NY 10007" },
  { city: "Singapore", country: "Singapore",        tz: "SGT",  addr: "1 Raffles Place · #40-01" },
];

export default function Contact() {
  const [dept, setDept] = useState("sales");
  const [form, setForm] = useState({ name: "", email: "", company: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/contact", { ...form, subject: `[${dept}] ${form.subject}` });
      toast.success("Thanks — we'll reply within our SLA.");
      setForm({ name: "", email: "", company: "", subject: "", message: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setBusy(false); }
  };

  const activeDept = DEPARTMENTS.find((d) => d.key === dept);

  return (
    <>
      <HeadMeta title="Contact" description={`Get in touch with ${BRAND.app_name} sales, support, enterprise or general enquiries.`} />
      <div className="relative">
        <GradientBg variant="soft" />
        <section className="relative mx-auto max-w-6xl px-6 pt-28 pb-16">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold">Contact</div>
            <h1 className="mt-4 text-5xl md:text-6xl font-black tracking-tight">Let's talk.</h1>
            <p className="mt-4 text-muted-foreground max-w-xl">
              Pick a department and we'll route your enquiry to the right team.
            </p>
          </Reveal>
        </section>

        <section className="mx-auto max-w-6xl px-6 pb-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {DEPARTMENTS.map((d) => {
              const Icon = d.icon;
              const isActive = dept === d.key;
              return (
                <button
                  key={d.key}
                  onClick={() => setDept(d.key)}
                  data-testid={`contact-dept-${d.key}`}
                  className={`text-left surface p-5 transition-colors ${isActive ? "border-primary/40 glow-violet" : "hover:border-white/20"}`}
                >
                  <Icon size={18} className={isActive ? "text-primary" : "text-muted-foreground"} />
                  <div className="mt-3 font-bold">{d.label}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{d.blurb}</div>
                  <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-primary/80">Reply · {d.sla}</div>
                </button>
              );
            })}
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-6 pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            <div className="lg:col-span-2 space-y-6">
              <div className="glass p-6">
                <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold">Or write directly</div>
                <div className="mt-3 font-mono text-sm">
                  <a href={`mailto:${activeDept.email()}`} className="text-primary hover:underline">{activeDept.email()}</a>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">{activeDept.blurb}</div>
              </div>
              <div className="glass p-6">
                <MessageCircle size={16} className="text-primary" />
                <div className="mt-3 font-semibold text-sm">Live chat</div>
                <div className="text-xs text-muted-foreground">Available Mon–Fri, 08:00–20:00 UTC. In-portal for logged-in customers.</div>
              </div>
              <div className="glass p-6">
                <Building size={16} className="text-primary" />
                <div className="mt-3 font-semibold text-sm">{BRAND.legal_company_name}</div>
                <div className="text-xs text-muted-foreground">{BRAND.company_address}</div>
              </div>
            </div>

            <form onSubmit={onSubmit} className="lg:col-span-3 glass-strong p-8 md:p-10 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Name"    value={form.name}    onChange={(v) => set("name", v)}    required tid="contact-name" />
                <Field label="Email"   value={form.email}   onChange={(v) => set("email", v)}   required type="email" tid="contact-email" />
                <Field label="Company (optional)" value={form.company} onChange={(v) => set("company", v)} tid="contact-company" />
                <Field label="Subject" value={form.subject} onChange={(v) => set("subject", v)} required tid="contact-subject" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Message</label>
                <textarea
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  rows={5}
                  required
                  data-testid="contact-message"
                  className="input-dark resize-none"
                />
              </div>
              <button type="submit" disabled={busy} data-testid="contact-submit" className="btn-primary w-full py-3">
                {busy ? "Sending…" : `Send to ${activeDept.label}`}
              </button>
            </form>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-6 pb-32">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold">Offices</div>
            <h2 className="mt-3 text-3xl font-black tracking-tight">Where we work</h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {OFFICES.map((o, i) => (
              <Reveal key={o.city} delay={i * 0.08}>
                <div className="surface p-6">
                  <div className="font-bold text-lg">{o.city}</div>
                  <div className="text-sm text-muted-foreground">{o.country}</div>
                  <div className="mt-4 pt-4 border-t border-white/[0.06] text-xs text-muted-foreground">{o.addr}</div>
                  <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-primary">{o.tz}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}

function Field({ label, value, onChange, required, type = "text", tid }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} data-testid={tid} className="input-dark" />
    </div>
  );
}
